import { useMemo, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router'
import { useUrlParams } from '../params'
import { useQuery } from '@tanstack/react-query'
import { Button } from '../../components/ui'
import { NODES_KEY, fetchNodes } from '../Nodes'
import { RELAY, mapTablePath, noPathUser, relayKeys, routePath, type NoPathStream, type RelayRoute, type RelayRouteList, type RouteState } from './api'
import { RelayDataBanner } from './banner'
import { useCenterNow } from './clock'
import { ago, ms, plural } from './format'
import { graceText } from '../../live-ui/grace'
import { useRelayLive } from './live'
import { NodeName, PathLine, roleAt } from './path'
import { errorText, useRelayGet } from './queries'
import { NO_PATH_TEXT, ROUTE_SPEC, reasonText } from './states'
import { Empty, EpochTag, ErrorBox, NetPanel, SkeletonRows, StateBadge } from './ui'

/*
 * Network › Routes (handoff/routes.md, design #routes, R0 read-only): every live
 * route with its stream, user, path (PathLine), state with the reason in words,
 * epoch, hop latency p50 / p99, the last resume and the source hold; problems
 * first. Filters (state chips, node, user), search and sort live in the URL
 * (?state=&node=&user=&q=&sort=). Streams with no route at all (no_path[] of the
 * list, SSE no_path) are a block of their own above the list. Live from the SSE
 * stream; without it the list is polled every 5 s. A row opens the route card.
 * No bulk actions (R2).
 */

type StateFilter = '' | 'problems' | RouteState
type Sort = 'state' | 'stream' | 'hop' | 'resume' | 'epoch' | 'created'

const PROBLEMS: RouteState[] = ['no_path', 'degraded', 'on_standby', 'switching']
const STATE_ORDER: RouteState[] = ['no_path', 'degraded', 'on_standby', 'switching', 'pending', 'active', 'ended']
const CHIPS: { value: StateFilter; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'problems', label: 'Problems' },
  { value: 'no_path', label: 'No path' },
  { value: 'degraded', label: 'Degraded' },
  { value: 'on_standby', label: 'On backup' },
  { value: 'switching', label: 'Switching' },
  { value: 'pending', label: 'Connecting' },
  { value: 'active', label: 'Active' },
]
const SORTS: { value: Sort; label: string }[] = [
  { value: 'state', label: 'Problems first' },
  { value: 'stream', label: 'Stream' },
  { value: 'hop', label: 'Hop latency p99' },
  { value: 'resume', label: 'Last resume' },
  { value: 'epoch', label: 'Epoch' },
  { value: 'created', label: 'Newest' },
]
const SEL = 'min-h-9 rounded-md border border-line-2 bg-bg px-2 text-[13px] text-text-soft focus-visible:border-accent max-md:min-h-[44px]'

interface Filter {
  state: StateFilter
  node: string
  user: string
  /** routes through one link (?link=<from>-<to>, from the link panel) */
  link: string
  q: string
  sort: Sort
}

function useFilter(): [Filter, (patch: Partial<Filter>) => void] {
  const { params: p, update } = useUrlParams()
  const f: Filter = {
    state: (p.get('state') ?? '') as StateFilter,
    node: p.get('node') ?? '',
    user: p.get('user') ?? '',
    link: p.get('link') ?? '',
    q: p.get('q') ?? '',
    sort: (SORTS.some((s) => s.value === p.get('sort')) ? p.get('sort') : 'state') as Sort,
  }
  // the shared helper starts from the URL as it is now (two quick changes must not undo each other)
  const set = (patch: Partial<Filter>) => update(Object.fromEntries(Object.entries(patch).map(([k, v]) => [k, k === 'sort' && v === 'state' ? '' : String(v ?? '')])))
  return [f, set]
}

/** The worst hop of a route: the highest p99 among its hops (R0: one hop). */
function hopLatency(r: RelayRoute): { p50?: number; p99?: number } {
  let best: { p50?: number; p99?: number } = {}
  for (const h of r.hops) {
    if (h.lat_p99_ms == null && h.lat_p50_ms == null) continue
    if (best.p99 == null || (h.lat_p99_ms ?? 0) > best.p99) best = { p50: h.lat_p50_ms, p99: h.lat_p99_ms }
  }
  return best
}

const RESUME_WORD = { ring: 'no loss', gap: 'frames lost', fresh: 'fresh start' } as const

function sortRoutes(rs: RelayRoute[], sort: Sort): RelayRoute[] {
  const byState = (a: RelayRoute, b: RelayRoute) => STATE_ORDER.indexOf(a.state) - STATE_ORDER.indexOf(b.state)
  const newest = (a: RelayRoute, b: RelayRoute) => Date.parse(b.changed_at) - Date.parse(a.changed_at) || a.route_id.localeCompare(b.route_id)
  const cmp: Record<Sort, (a: RelayRoute, b: RelayRoute) => number> = {
    state: (a, b) => byState(a, b) || newest(a, b),
    stream: (a, b) => a.stream.localeCompare(b.stream) || a.route_id.localeCompare(b.route_id),
    hop: (a, b) => (hopLatency(b).p99 ?? -1) - (hopLatency(a).p99 ?? -1) || byState(a, b),
    resume: (a, b) => (Date.parse(b.last_resume?.at ?? '') || 0) - (Date.parse(a.last_resume?.at ?? '') || 0) || byState(a, b),
    epoch: (a, b) => b.epoch - a.epoch || byState(a, b),
    created: (a, b) => Date.parse(b.created_at) - Date.parse(a.created_at) || a.route_id.localeCompare(b.route_id),
  }
  return [...rs].sort(cmp[sort])
}

const userText = (r: RelayRoute) => r.user?.email ?? (r.user ? `user #${r.user.id}` : '—')

export function RoutesListPage() {
  const live = useRelayLive()
  const [f, setF] = useFilter()
  const now = useCenterNow(1000)
  const list = useRelayGet<RelayRouteList>(relayKeys.routesList, `${RELAY}/routes?limit=500`, live, { poll: true })
  const nodesQ = useQuery({ queryKey: NODES_KEY, queryFn: fetchNodes, refetchInterval: 10_000, enabled: live.available === true })
  const nodes = useMemo(() => new Map((nodesQ.data ?? []).map((n) => [n.id, n])), [nodesQ.data])
  const rolesOf = (id: string) => nodes.get(id)?.roles
  const offline = (id: string) => {
    const s = live.nodes.get(id)?.status ?? nodes.get(id)?.status
    return s === 'offline'
  }

  // The stream's view while it is open, the list's otherwise (lost stream: polled every 5 s).
  const fromLive = live.sse === 'open' && live.loaded
  const routes = useMemo(
    () => (fromLive ? [...live.routes.values()] : (list.data?.data.items ?? []).filter((r) => r.state !== 'ended')),
    [fromLive, live.routes, list.data],
  )
  const noPath = useMemo(() => (fromLive ? [...live.noPath.values()] : (list.data?.data.no_path ?? [])), [fromLive, live.noPath, list.data])

  const users = useMemo(() => {
    const m = new Map<number, string>()
    for (const r of routes) if (r.user) m.set(r.user.id, r.user.email ?? `user #${r.user.id}`)
    // streams without a route have an owner too (user_id; user with the e-mail from API-UI)
    for (const x of noPath) {
      const id = noPathUser(x)
      if (id !== undefined && (!m.has(id) || x.user?.email)) m.set(id, x.user?.email ?? m.get(id) ?? `user #${id}`)
    }
    return [...m].sort((a, b) => a[1].localeCompare(b[1]))
  }, [routes, noPath])
  const emailOf = (id: number | undefined) => (id === undefined ? undefined : users.find(([u]) => u === id)?.[1])
  // the link of ?link=: its ends from the stream's links, else from the id (<from>-<to>)
  const link = useMemo(() => {
    if (!f.link) return null
    const l = live.links.get(f.link)
    if (l) return { id: f.link, from: l.from.id, to: l.to.id, fromName: l.from.name, toName: l.to.name, routeIds: new Set(l.route_ids) }
    const m = /^(.+?)-(.+)$/.exec(f.link)
    return m ? { id: f.link, from: m[1]!, to: m[2]!, fromName: nodes.get(m[1]!)?.name ?? m[1]!, toName: nodes.get(m[2]!)?.name ?? m[2]!, routeIds: null } : null
  }, [f.link, live.links, nodes])
  const nodeOptions = useMemo(() => {
    const m = new Map<string, string>()
    for (const r of routes) for (const p of r.path) m.set(p.id, p.name)
    for (const x of noPath) m.set(x.ingress_node_id, nodes.get(x.ingress_node_id)?.name ?? x.ingress_node_id)
    return [...m].sort((a, b) => a[1].localeCompare(b[1]))
  }, [routes, noPath, nodes])

  // node, user and search narrow the scope; the state chips count inside it
  const ql = f.q.trim().toLowerCase()
  const scoped = useMemo(
    () =>
      routes.filter((r) => {
        if (f.node && !r.path.some((p) => p.id === f.node)) return false
        if (f.user && String(r.user?.id ?? '') !== f.user) return false
        if (link && !(link.routeIds?.has(r.route_id) || r.hops.some((h) => h.from.id === link.from && h.to.id === link.to))) return false
        if (ql && !`${r.route_id} ${r.stream} ${userText(r)} ${r.path.map((p) => `${p.id} ${p.name}`).join(' ')}`.toLowerCase().includes(ql)) return false
        return true
      }),
    [routes, f.node, f.user, link, ql],
  )
  const counts = useMemo(() => {
    const c: Record<string, number> = { '': scoped.length, problems: 0 }
    for (const r of scoped) {
      c[r.state] = (c[r.state] ?? 0) + 1
      if (PROBLEMS.includes(r.state)) c.problems!++
    }
    return c
  }, [scoped])
  const shown = useMemo(
    () => sortRoutes(scoped.filter((r) => (f.state === 'problems' ? PROBLEMS.includes(r.state) : !f.state || r.state === f.state)), f.sort),
    [scoped, f.state, f.sort],
  )
  // streams without a route: filtered by node (the ingress), user (user_id / user of the center) and
  // search; they are no_path and cross no link
  const noPathShown = useMemo(
    () =>
      noPath
        .filter(() => !link && (!f.state || f.state === 'problems' || f.state === 'no_path'))
        .filter((x) => !f.user || String(noPathUser(x) ?? '') === f.user)
        .filter((x) => !f.node || x.ingress_node_id === f.node)
        .filter((x) => !ql || `${x.stream} ${x.ingress_node_id} ${nodes.get(x.ingress_node_id)?.name ?? ''} ${x.user?.email ?? ''}`.toLowerCase().includes(ql))
        .sort((a, b) => Date.parse(a.since) - Date.parse(b.since)),
    [noPath, link, f.user, f.state, f.node, ql, nodes],
  )
  const filtered = !!(f.state || f.node || f.user || f.q || f.link)
  const reset = () => setF({ state: '', node: '', user: '', q: '', link: '' })
  const loading = !fromLive && list.isPending && live.available !== false
  const failed = !fromLive && list.error && !list.data

  return (
    <div className="flex flex-col gap-4" data-testid="network-routes" data-source={fromLive ? 'live' : 'poll'}>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold">Routes</h1>
        <span className="num text-sm text-muted" data-testid="routes-total">
          {plural(routes.length, 'live route')}
          {noPath.length ? ` · ${noPath.length} without a route` : ''}
        </span>
        <span className="ml-auto text-xs text-muted">{fromLive ? 'live' : live.available === false ? '' : 'polling every 5 s'}</span>
      </div>
      <RelayDataBanner live={live} />

      {live.available === false ? (
        <Empty title="No routing network on this center" testId="routes-unavailable">
          This center has no relay API (GET /api/admin/relay/settings answered 404). Routes appear after an update of the center with the routing network.
        </Empty>
      ) : (
        <>
          {!loading && !failed && (routes.length > 0 || noPath.length > 0) && (
            <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filter by state">
              {CHIPS.filter((c) => c.value === '' || c.value === 'problems' || (counts[c.value] ?? 0) > 0 || f.state === c.value).map((c) => {
                const spec = c.value && c.value !== 'problems' ? ROUTE_SPEC[c.value] : null
                return (
                  <button
                    key={c.value || 'all'}
                    type="button"
                    aria-pressed={f.state === c.value}
                    data-testid={`routes-chip-${c.value || 'all'}`}
                    onClick={() => setF({ state: c.value })}
                    className={`num inline-flex min-h-8 items-center gap-1.5 rounded-md border px-3 text-[13px] max-md:min-h-[44px] ${f.state === c.value ? 'border-live/50 bg-live/[.12] text-live' : 'border-line-2 text-text-soft hover:text-text'}`}
                  >
                    {spec && <span aria-hidden>{spec.glyph}</span>}
                    {c.label} <span className="font-mono">{counts[c.value] ?? 0}</span>
                  </button>
                )
              })}
            </div>
          )}

          {link && (
            <p className="flex flex-wrap items-center gap-2 rounded-md border border-line-2 px-3 py-2 text-sm" data-testid="routes-link-filter" data-link={link.id}>
              <span className="text-muted">Through the link</span>
              <Link to={mapTablePath({ link: link.id })} className="font-mono text-text-soft underline underline-offset-2">
                {link.fromName} → {link.toName}
              </Link>
              <button type="button" className="ml-auto inline-flex min-h-8 items-center px-2 text-xs text-muted hover:text-text max-md:min-h-[44px]" data-testid="routes-link-clear" onClick={() => setF({ link: '' })}>
                ✕ Any link
              </button>
            </p>
          )}

          {noPathShown.length > 0 && <NoPathBlock items={noPathShown} nameOf={(id) => nodes.get(id)?.name ?? id} emailOf={emailOf} rolesOf={rolesOf} offline={offline} />}

          <section className="rs-corners min-w-0 rounded-lg border border-line" aria-label="Routes">
            <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-3">
              <label className="flex min-h-9 min-w-0 flex-1 basis-[220px] items-center gap-2 rounded-md border border-line-2 bg-bg px-2.5 focus-within:border-accent focus-within:shadow-focus max-md:min-h-[44px] md:max-w-[300px]">
                <span className="text-muted" aria-hidden>
                  ⌕
                </span>
                <input
                  value={f.q}
                  onChange={(e) => setF({ q: e.target.value })}
                  placeholder="Route, stream, email, node"
                  aria-label="Search routes"
                  data-testid="routes-search"
                  className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted"
                />
              </label>
              <select aria-label="Through node" data-testid="routes-node" className={SEL} value={f.node} onChange={(e) => setF({ node: e.target.value })}>
                <option value="">Any node</option>
                {nodeOptions.map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </select>
              <select aria-label="User" data-testid="routes-user" className={SEL} value={f.user} onChange={(e) => setF({ user: e.target.value })}>
                <option value="">Any user</option>
                {users.map(([id, email]) => (
                  <option key={id} value={String(id)}>
                    {email}
                  </option>
                ))}
              </select>
              <select aria-label="Sort by" data-testid="routes-sort" className={SEL} value={f.sort} onChange={(e) => setF({ sort: e.target.value as Sort })}>
                {SORTS.map((s) => (
                  <option key={s.value} value={s.value}>
                    Sort: {s.label}
                  </option>
                ))}
              </select>
              {filtered && (
                <Button variant="quiet" className="max-md:min-h-[44px]" data-testid="routes-reset" onClick={reset}>
                  Reset filters
                </Button>
              )}
              <span className="num ml-auto text-xs text-muted" data-testid="routes-shown">
                {shown.length} shown
              </span>
            </div>

            {loading ? (
              <div className="p-4">
                <SkeletonRows rows={5} cols={8} />
              </div>
            ) : failed ? (
              <div className="p-4">
                <ErrorBox testId="routes-error" text={errorText(`${RELAY}/routes`, list.error)} busy={list.isFetching} onRetry={() => void list.refetch()} />
              </div>
            ) : routes.length === 0 ? (
              <div className="p-4">
                <Empty title="No routes now" testId="routes-empty">
                  A route appears when a stream goes through two nodes: OBS connects to an ingress node with egress via center, and the platforms are served from a hub.
                  Streams that stay on one node have no route.
                </Empty>
              </div>
            ) : shown.length === 0 ? (
              <div className="p-4">
                <Empty
                  title="No routes match the filters"
                  testId="routes-none-match"
                  action={
                    <Button onClick={reset} data-testid="routes-reset-empty">
                      Reset filters
                    </Button>
                  }
                />
              </div>
            ) : (
              <>
                <RoutesTable routes={shown} now={now} rolesOf={rolesOf} offline={offline} sort={f.sort} onSort={(s) => setF({ sort: s })} />
                <RouteCards routes={shown} now={now} rolesOf={rolesOf} offline={offline} />
              </>
            )}
          </section>

          {routes.length > 0 && (
            <p className="rounded-lg border border-dashed border-line-2 px-4 py-3 text-sm text-muted" data-testid="routes-r0-note">
              <b className="text-text-soft">Single-hop routes, read-only.</b> Each stream goes from its ingress straight to the hub. Moving routes, standbys and relays come later.
            </p>
          )}
        </>
      )}
    </div>
  )
}

/** Streams on a via-center node that have no route at all (no hub ready, capacity, …). */
function NoPathBlock({ items, nameOf, emailOf, rolesOf, offline }: {
  items: NoPathStream[]
  nameOf: (id: string) => string
  emailOf: (id: number | undefined) => string | undefined
  rolesOf: (id: string) => readonly string[] | undefined
  offline: (id: string) => boolean
}) {
  return (
    <NetPanel
      pad={false}
      testId="routes-no-path"
      className="border-fail/50"
      title={
        <span className="flex items-center gap-2">
          <StateBadge spec={ROUTE_SPEC.no_path} />
          <h2 className="text-[15px] font-semibold">Streams without a route</h2>
          <span className="num font-mono text-xs text-muted">{items.length}</span>
        </span>
      }
      aside={<span className="text-xs text-muted">Their platforms get nothing through the relay; outputs hold the sessions for the source grace.</span>}
    >
      <ul className="divide-y divide-line">
        {items.map((x) => (
          <li key={x.stream} data-testid={`no-path-${x.stream}`} data-reason={x.reason} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5">
            <span className="font-mono text-[13px] font-semibold">{x.stream}</span>
            {noPathUser(x) !== undefined && (
              <span className="text-xs text-text-soft" data-testid={`no-path-user-${x.stream}`}>
                {x.user?.email ?? emailOf(noPathUser(x)) ?? `user #${noPathUser(x)}`}
              </span>
            )}
            <span className="text-xs text-muted">
              on <NodeName node={{ id: x.ingress_node_id, name: nameOf(x.ingress_node_id), region: '' }} role={roleAt(0, 2, rolesOf(x.ingress_node_id))} offline={offline(x.ingress_node_id)} />
            </span>
            <span className="text-[13px] text-fail">{NO_PATH_TEXT[x.reason] ?? x.reason.replace(/_/g, ' ')}</span>
            <span className="num ml-auto font-mono text-xs text-muted" title={x.since}>
              since {ago(x.since)}
            </span>
          </li>
        ))}
      </ul>
    </NetPanel>
  )
}

function Hold({ r, now, card }: { r: RelayRoute; now: number; /** the phone card: no test id (the table has it) */ card?: boolean }) {
  const tid = card ? undefined : `route-hold-${r.route_id}`
  const left = r.source_grace_until ? Math.ceil((Date.parse(r.source_grace_until) - now) / 1000) : NaN
  if (left > 0)
    return (
      <span className="num font-mono text-xs text-warn" data-testid={tid} data-left={left} title="OBS left: the outputs hold the platform sessions until then, and the route ends unless OBS returns">
        ↻ {left} s
      </span>
    )
  // the hold in force (API-UI): what the outputs keep the platforms for if OBS drops
  const set = r.source_grace_ms
  if (r.source && !r.source.live)
    return (
      <span className="text-xs text-muted" data-testid={tid}>
        source away{set !== undefined ? ` · ${graceText(set)}` : ''}
      </span>
    )
  if (set !== undefined)
    return (
      <span className="num font-mono text-xs text-text-soft" data-testid={tid} data-hold-ms={set} title={`OBS is connected. If it drops, the outputs hold the platform sessions for ${graceText(set)}`}>
        {graceText(set)}
      </span>
    )
  return (
    <span className="text-xs text-faint" data-testid={tid} title="OBS is connected: nothing is held">
      —
    </span>
  )
}

function Resume({ r }: { r: RelayRoute }) {
  const x = r.last_resume
  if (!x?.at) return <span className="text-xs text-faint">none</span>
  const word = x.result ? RESUME_WORD[x.result] : 'resumed'
  return (
    <span className="flex min-w-0 flex-col" title={`${x.at}${x.outage_ms != null ? ` · outage ${ms(x.outage_ms)}` : ''}`}>
      <span className="num font-mono text-xs whitespace-nowrap">{ago(x.at)}</span>
      <span className={`truncate text-[11px] ${x.result === 'gap' ? 'text-warn' : 'text-muted'}`}>
        {x.result === 'gap' && x.gap_frames ? `${x.gap_frames} ${word}` : word}
        {x.outage_ms != null ? ` · ${ms(x.outage_ms)}` : ''}
      </span>
    </span>
  )
}

function Reason({ r }: { r: RelayRoute }) {
  const spec = ROUTE_SPEC[r.state]
  const reason = reasonText(r.state_reason)
  if (!reason) return null
  return <span className={`text-[11px] leading-snug ${spec.color === 'fail' ? 'text-fail' : spec.color === 'warn' ? 'text-warn' : 'text-muted'}`}>{reason}</span>
}

type Col = { key: string; label: ReactNode; w: number; sort?: Sort; right?: boolean }

function RoutesTable({ routes, now, rolesOf, offline, sort, onSort }: {
  routes: RelayRoute[]
  now: number
  rolesOf: (id: string) => readonly string[] | undefined
  offline: (id: string) => boolean
  sort: Sort
  onSort: (s: Sort) => void
}) {
  const nav = useNavigate()
  const cols: Col[] = [
    { key: 'stream', label: 'Stream', w: 12, sort: 'stream' },
    { key: 'user', label: 'User', w: 13 },
    { key: 'path', label: 'Path', w: 24 },
    { key: 'state', label: 'State', w: 14, sort: 'state' },
    { key: 'epoch', label: 'Epoch', w: 7, sort: 'epoch' },
    { key: 'hop', label: <span title="Latency of the worst hop: p50 / p99">Hop p50 / p99</span>, w: 10, sort: 'hop', right: true },
    { key: 'resume', label: 'Last resume', w: 11, sort: 'resume' },
    { key: 'hold', label: <span title="While OBS is away: how long the outputs still hold the platform sessions; otherwise the hold in force">Hold</span>, w: 7 },
  ]
  const sum = cols.reduce((a, c) => a + c.w, 0)
  return (
    <div className="hidden md:block">
      <table data-testid="routes-table" className="w-full table-fixed text-left text-sm">
        <caption className="sr-only">Live routes, {SORTS.find((s) => s.value === sort)?.label.toLowerCase()}</caption>
        <colgroup>
          {cols.map((c) => (
            <col key={c.key} style={{ width: `${((c.w / sum) * 100).toFixed(2)}%` }} />
          ))}
        </colgroup>
        <thead className="border-b border-line text-xs text-muted">
          <tr>
            {cols.map((c) => (
              <th scope="col" key={c.key} className={`px-3 py-2 font-medium first:pl-4 last:pr-4 ${c.right ? 'text-right' : ''}`} aria-sort={c.sort && sort === c.sort ? (c.sort === 'stream' ? 'ascending' : 'descending') : undefined}>
                {c.sort ? (
                  <button type="button" onClick={() => onSort(c.sort!)} className={`inline-flex items-center gap-1 hover:text-text ${sort === c.sort ? 'text-text' : ''}`}>
                    {c.label}
                    {sort === c.sort && <span aria-hidden>▾</span>}
                  </button>
                ) : (
                  c.label
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {routes.map((r) => {
            const lat = hopLatency(r)
            const open = () => void nav(routePath(r.route_id))
            return (
              <tr
                key={r.route_id}
                data-testid={`route-row-${r.route_id}`}
                data-state={r.state}
                onClick={(e) => {
                  if (!(e.target as HTMLElement).closest('a,button')) open()
                }}
                className="cursor-pointer hover:bg-panel [&>td]:overflow-hidden [&>td]:px-3 [&>td]:py-2 [&>td]:align-top [&>td:first-child]:pl-4 [&>td:last-child]:pr-4"
              >
                <td>
                  <Link to={routePath(r.route_id)} className="block truncate font-mono text-[13px] font-semibold hover:underline" data-testid={`route-link-${r.route_id}`} title={`Open route ${r.route_id}`}>
                    {r.stream}
                  </Link>
                  <span className="block truncate font-mono text-[11px] text-muted">{r.route_id}</span>
                </td>
                <td>
                  <span className="block truncate text-xs text-text-soft" title={userText(r)}>
                    {userText(r)}
                  </span>
                </td>
                <td>
                  <PathLine path={r.path} hops={r.hops} rolesOf={rolesOf} offline={offline} />
                </td>
                <td>
                  <span className="flex min-w-0 flex-col items-start gap-1" data-testid={`route-state-${r.route_id}`}>
                    <StateBadge spec={ROUTE_SPEC[r.state]} />
                    <Reason r={r} />
                  </span>
                </td>
                <td>
                  <EpochTag e={r.epoch} />
                </td>
                <td className="num text-right font-mono text-xs whitespace-nowrap" data-testid={`route-hop-${r.route_id}`}>
                  {lat.p50 == null && lat.p99 == null ? <span className="text-faint">—</span> : `${ms(lat.p50)} / ${ms(lat.p99)}`}
                </td>
                <td>
                  <Resume r={r} />
                </td>
                <td>
                  <Hold r={r} now={now} />
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/** Phone: one card per route, state first; the card opens the route card. */
function RouteCards({ routes, now, rolesOf, offline }: { routes: RelayRoute[]; now: number; rolesOf: (id: string) => readonly string[] | undefined; offline: (id: string) => boolean }) {
  return (
    <ul className="divide-y divide-line md:hidden" data-testid="routes-cards">
      {routes.map((r) => {
        const lat = hopLatency(r)
        return (
          <li key={r.route_id} data-testid={`route-card-${r.route_id}`} data-state={r.state} className="flex flex-col gap-1.5 px-4 py-3">
            <Link to={routePath(r.route_id)} className="flex min-h-[44px] items-start justify-between gap-3">
              <span className="flex min-w-0 flex-col">
                <span className="truncate font-mono text-[15px] font-semibold">{r.stream}</span>
                <span className="truncate font-mono text-[11px] text-muted">
                  {r.route_id} · e{r.epoch} · {userText(r)}
                </span>
              </span>
              <StateBadge spec={ROUTE_SPEC[r.state]} />
            </Link>
            <Reason r={r} />
            <PathLine path={r.path} hops={r.hops} rolesOf={rolesOf} offline={offline} />
            <span className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
              <span className="num font-mono">hop {lat.p99 == null ? '—' : `${ms(lat.p50)} / ${ms(lat.p99)}`}</span>
              <Resume r={r} />
              <Hold r={r} now={now} card />
            </span>
          </li>
        )
      })}
    </ul>
  )
}
